import { ArrowBack } from "@mui/icons-material";
import { Call, Email, CheckCircle, Delete } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  OutlinedInput,
  Paper,
  Dialog,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { Formik, Form, useFormikContext, Field } from "formik";
import InputField from "../../common/FormFields/InputField";
import TextAreaField from "../../common/FormFields/TextAreaField";
import SelectField from "../../common/FormFields/SelectField";
import AutocompleteField from "../../common/FormFields/AutocompleteField";
import MarinerAutocompleteField from "../../common/FormFields/MarinerAutocompleteField";
import { colorFromName } from "../../common/ColorFromName";
import {
  AB_VARIANT_RATINGS,
  DisabledJobRatings,
  JobOrder,
  JobRating,
  JobRatingLabel,
  LicensedJobType,
  LicensedJobTypeLabel,
  Publish,
  PublishLabel,
  ReliefReason,
  ReliefReasonLabel,
  UnLicensedJobType,
  UnLicensedJobTypeLabel,
} from "../../common/JobOrder";
import {
  SignerType,
  SignerTypeLabel,
  TravelDocumentOperationType,
} from "../../common/TravelDocument";
import convertDateToString from "../../util/ConvertDateToString";
import ProfileDocuments from "../marinerProfile/ProfileDocuments";
import ChecklistDisplay from "./ChecklistDisplay";
import TravelDocuments from "./TravelDocuments";
import "./JobOrder.scss";
import { RegistrationStatus, User, UserGroup } from "../../common/User";
import { DisabledPorts, PortMasterLabel } from "../../common/Port";
import {
  useGetJobOrderQuery,
  useLazyGetJobOrderHistoryQuery,
  useCreateJobOrderMutation,
  useUpdateJobOrderMutation,
  useMarkJobOrderDeletedMutation,
} from "../../redux/api/jobOrder";
import { useGetUsersByGroupQuery } from "../../redux/api/user";
import { useGetPortsQuery, useGetVesselsQuery } from "../../redux/api/vessel";
import formData from "./formValidation/formData";
import initialValues from "./formValidation/initialValues";
import validationSchema from "./formValidation/validationSchema";
import {
  ActionByType,
  ProfileDocumentOperationType,
} from "../../common/ProfileDocument";
import EmailModal from "../email/EmailModal";
import {
  Gender,
  GenderSymbol,
  MarinerProfile,
  MarinerType,
  RatingTypeMap,
} from "../../common/MarinerProfile";
import moment from "moment";
import { useGetDispatchesQuery } from "../../redux/api/dispatch";
import { DispatchSearchParams } from "../../common/Dispatch";
import { usePrompt } from "../../util/usePrompt";
import useDocumentTitle from "../../util/useDocumentTitle";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { EmailType } from "../../common/EmailDetails";
import JobRequirementsDisplay from "../jobBoard/JobRequirementsDisplay";
import { useGetMMCRequirementQuery } from "../../redux/api/mmc";
import { marinerStatusText } from "../../common/CognitoUser/MarinerStatusText";
import { registrationEmailButton } from "../../common/CognitoUser/RegistrationEmailButton";
import {
  useAdminCreateProfileUserMutation,
  useAdminResendRegistrationEmailMutation,
} from "../../redux/api/mariner";
import { formatDateString } from "../../common/FormatDateString";
import {
  JobOrderChange,
  FieldName,
  FieldNameLabel,
} from "../../common/JobOrderHistory";
import { useGetAllUnionsQuery } from "../../redux/api/union";

const JobOrderForm = () => {
  useDocumentTitle("Job Order Details");
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS;
  const isMariner = (user?.userGroup as UserGroup) === UserGroup.MARINERS;
  const [ports, setPorts] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currDeletedNote, setCurrDeletedNote] = useState("");
  const [showJobOrderHistory, setShowJobOrderHistory] = useState(false);
  const [jobOrderChangeList, setJobOrderChangeList] =
    useState<JobOrderChange[]>();

  const { formId, formField } = formData;
  const {
    createdDate,
    fillByDate,
    smis,
    vessel,
    assignee,
    onsigner,
    onsignerLeavingFrom,
    onsignerDestination,
    onsignerTravelTimestamp,
    onsignerAccomodation,
    onsignerPickupDate,
    onsignerDropoffDate,
    onsignerNotes,
    onsignerTwicTsaNum,
    onsignerDateOfBirth,
    onsignerAgentUnion,
    onsignerUnionHall,
    offsigner,
    offsignerLeavingFrom,
    offsignerDestination,
    offsignerTravelTimestamp,
    offsignerAccomodation,
    offsignerPickupDate,
    offsignerDropoffDate,
    offsignerNotes,
    checklistRoot,
    travelDocuments,
    rating,
    port,
    reliefReason,
    flightNotice,
    reliefTime,
    offsignerTwicTsaNum,
    offsignerDateOfBirth,
    covid19Testing,
    testingLocation,
    jobType,
    publish,
    note,
    extra,
  } = formField;

  const { id } = useParams();

  const [getJobOrderChanges, results] = useLazyGetJobOrderHistoryQuery();

  const {
    data: jobOrderFromApi,
    isLoading,
    isFetching,
    refetch,
  } = useGetJobOrderQuery(id !== undefined && id !== "new" ? id : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const dispatchSearchParams: DispatchSearchParams = {
    vesselId: undefined,
    fleetId: undefined,
    marinerIds: jobOrderFromApi?.offsigner?.id
      ? [jobOrderFromApi.offsigner.id]
      : undefined,
    ratings: undefined,
    current: true,
  };
  const { data: dispatch } = useGetDispatchesQuery(
    jobOrderFromApi?.offsigner?.id ? dispatchSearchParams : skipToken
  );
  const { data: portsFromApi } = useGetPortsQuery();

  const isABVariantJobOrder =
    jobOrderFromApi?.rating != null &&
    AB_VARIANT_RATINGS.has(jobOrderFromApi.rating);

  const { data: mmcRequirement } = useGetMMCRequirementQuery(
    jobOrderFromApi?.vessel?.id != null && jobOrderFromApi?.rating != null
      ? { vessel: jobOrderFromApi.vessel.id, rating: jobOrderFromApi.rating }
      : skipToken
  );

  const requiredCourseNames = new Set(
    mmcRequirement?.courseList
      .filter((c) => c.required)
      .map((c) => c.courseName) ?? []
  );

  const blockNaCourseNames = new Set(
    mmcRequirement?.courseList
      .filter((c) => c.blockNa)
      .map((c) => c.courseName) ?? []
  );

  const { data: vessels } = useGetVesselsQuery({});
  const { data: specialists } = useGetUsersByGroupQuery(
    UserGroup.CREWING_SPECIALISTS
  );
  const { data: managers } = useGetUsersByGroupQuery(
    UserGroup.CREWING_MANAGERS
  );
  const { data: evalSpecialists } =
      useGetUsersByGroupQuery(UserGroup.CREWING_EVAL_SPECIALISTS);
  const { data: unionHalls } = useGetAllUnionsQuery();

  const assignees = useMemo(() => {
    return [...(specialists || []), ...(managers || []), ...(evalSpecialists || [])];
  }, [specialists, managers, evalSpecialists]);

  const [createJobOrder] = useCreateJobOrderMutation();
  const [updateJobOrder] = useUpdateJobOrderMutation();
  const [markJobOrderDeleted] = useMarkJobOrderDeletedMutation();
  const [adminCreateProfileUser] = useAdminCreateProfileUserMutation();
  const [adminResendRegistrationEmail] =
    useAdminResendRegistrationEmailMutation();

  // temporarily store the status in these variable, these change with each render and are loaded when the form is loaded
  let onsignerRegistrationStatus = "";
  let offsignerRegistrationStatus = "";
  let onsignerEmail = false;
  let offsignerEmail = false;
  let showCovidFields = false;

  let getInitialValues = () => {
    if (!isLoading && !isFetching && id !== undefined && id !== "new") {
      let res: any = JSON.parse(JSON.stringify(jobOrderFromApi));
      Object.keys(res).forEach((key) => {
        res[key] = res[key] === null ? "" : res[key];
      });
      // store the user status
      onsignerRegistrationStatus =
        res["onsigner"] && res["onsigner"]["cognitoUser"]
          ? res["onsigner"]["cognitoUser"]["status"]
          : "";
      offsignerRegistrationStatus =
        res["offsigner"] && res["offsigner"]["cognitoUser"]
          ? res["offsigner"]["cognitoUser"]["status"]
          : "";
      // store whether the profiles have an email
      onsignerEmail = res["onsigner"] && !!res["onsigner"]["email"];
      offsignerEmail = res["offsigner"] && !!res["offsigner"]["email"];
      // store whether to show covid fields
      showCovidFields = !!res["covid19Testing"] || !!res["testingLocation"];

      return {
        [createdDate.name]:
          res["createdDate"] !== undefined && res["createdDate"] !== ""
            ? convertDateToString(new Date(res["createdDate"]))
            : res["createdDate"],
        [fillByDate.name]:
          res["fillByDate"] !== undefined && res["fillByDate"] !== ""
            ? convertDateToString(new Date(res["fillByDate"]))
            : res["fillByDate"],
        [smis.name]: res["smis"],
        [vessel.name]: res["vessel"],
        [assignee.name]: res["assignee"],
        [onsigner.name]: res["onsigner"],
        [offsigner.name]: res["offsigner"],
        [onsignerLeavingFrom.name]: res["onsignerLeavingFrom"],
        [onsignerDestination.name]: res["onsignerDestination"],
        [onsignerTravelTimestamp.name]: res["onsignerTravelTimestamp"],
        [onsignerAccomodation.name]: res["onsignerAccomodation"],
        [onsignerPickupDate.name]:
          res["onsignerPickupDate"] !== undefined &&
          res["onsignerPickupDate"] !== ""
            ? convertDateToString(new Date(res["onsignerPickupDate"]))
            : res["onsignerPickupDate"],
        [onsignerDropoffDate.name]:
          res["onsignerDropoffDate"] !== undefined &&
          res["onsignerDropoffDate"] !== ""
            ? convertDateToString(new Date(res["onsignerDropoffDate"]))
            : res["onsignerDropoffDate"],
        [onsignerNotes.name]: res["onsignerNotes"],
        [offsignerLeavingFrom.name]: res["offsignerLeavingFrom"],
        [offsignerDestination.name]: res["offsignerDestination"],
        [offsignerTravelTimestamp.name]: res["offsignerTravelTimestamp"],
        [offsignerAccomodation.name]: res["offsignerAccomodation"],
        [offsignerPickupDate.name]:
          res["offsignerPickupDate"] !== undefined &&
          res["offsignerPickupDate"] !== ""
            ? convertDateToString(new Date(res["offsignerPickupDate"]))
            : res["offsignerPickupDate"],
        [offsignerDropoffDate.name]:
          res["offsignerDropoffDate"] !== undefined &&
          res["offsignerDropoffDate"] !== ""
            ? convertDateToString(new Date(res["offsignerDropoffDate"]))
            : res["offsignerDropoffDate"],
        [offsignerNotes.name]: res["offsignerNotes"],
        [checklistRoot.name]: res["checklistRoot"],
        [travelDocuments.name]: res["travelDocuments"],
        [rating.name]: res["rating"],
        [port.name]: res["port"],
        [reliefReason.name]: res["reliefReason"],
        [flightNotice.name]: res["flightNotice"],
        [reliefTime.name]: res["reliefTime"],
        // offsigner and onsigner attributes are not guaranteed to exist, so handle falsey
        [offsignerTwicTsaNum.name]: res["offsigner"]["twicTsaNum"] || "",
        [offsignerDateOfBirth.name]: res["offsigner"]["dateOfBirth"]
          ? convertDateToString(new Date(res["offsigner"]["dateOfBirth"]))
          : "",
        [onsignerTwicTsaNum.name]: res["onsigner"]["twicTsaNum"] || "",
        [onsignerDateOfBirth.name]: res["onsigner"]["dateOfBirth"]
          ? convertDateToString(new Date(res["onsigner"]["dateOfBirth"]))
          : "",
        [onsignerAgentUnion.name]: res["onsigner"]["agentUnion"] || "",
        [onsignerUnionHall.name]: res["onsignerUnionHall"],
        [covid19Testing.name]: res["covid19Testing"] || "",
        [testingLocation.name]: res["testingLocation"] || "",
        [jobType.name]: res["jobType"] || "",
        [publish.name]: !!res["publish"] ? res["publish"] : Publish.CREATED,
        [note.name]: res["note"] || "",
        [extra.name]: (res["extra"] as boolean) || false,
      };
    } else {
      return initialValues;
    }
  };

  useEffect(() => {
    const vessel = jobOrderFromApi?.vessel;
    let ports;
    if (vessel !== undefined && !!vessel.ports) {
      ports = vessel.ports;
    } else {
      ports = portsFromApi;
      //filter out disabled ports
      ports = ports?.filter(
        (port) => !DisabledPorts.some((dis) => PortMasterLabel[dis] === port)
      );
    }
    if (!!ports) setPorts(ports);
  }, [jobOrderFromApi?.vessel, portsFromApi]);

  useEffect(() => {
    if (results) {
      setJobOrderChangeList(results.data);
    }
  }, [results]);

  const handleSignerCallback = (
    signerType: SignerType,
    mariner: MarinerProfile | undefined,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(
      `${SignerTypeLabel[signerType as SignerType]}DateOfBirth`,
      mariner?.dateOfBirth
        ? convertDateToString(new Date(mariner.dateOfBirth))
        : ""
    );
    setFieldValue(
      `${SignerTypeLabel[signerType as SignerType]}TwicTsaNum`,
      mariner?.twicTsaNum ? mariner.twicTsaNum : ""
    );
    setFieldValue(
      `${SignerTypeLabel[signerType as SignerType]}AgentUnion`,
      mariner?.agentUnion ? mariner.agentUnion : ""
    );
  };

  const handleProfileDocumentCallback = (
    operationType: ProfileDocumentOperationType
  ) => {
    if (operationType !== ProfileDocumentOperationType.LOAD) {
      toast.success(
        <p>Onsigner document {operationType.toLowerCase()} successful.</p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    }
  };

  const convertFieldValueToDescription = (
    fieldName: FieldName,
    fieldValue: string | undefined
  ) => {
    if (!fieldValue) {
      return fieldValue;
    }
    switch (fieldName) {
      case FieldName.relief_reason:
        return ReliefReasonLabel[fieldValue as ReliefReason];
      case FieldName.publish:
        return PublishLabel[fieldValue as Publish];
      case FieldName.job_type:
        return Object.values(LicensedJobType).includes(
          fieldValue as LicensedJobType
        )
          ? LicensedJobTypeLabel[fieldValue as LicensedJobType]
          : UnLicensedJobTypeLabel[fieldValue as UnLicensedJobType];
      default:
        return fieldValue;
    }
  };

  const handleTravelDocumentCallback = (
    signer: SignerType,
    operationType: TravelDocumentOperationType
  ) => {
    if (operationType !== TravelDocumentOperationType.LOAD) {
      toast.success(
        <p>
          {SignerTypeLabel[signer as SignerType]} travel document{" "}
          {operationType.toLowerCase()} successful.
        </p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
    }
  };

  const handleEmailCallBack = () => {
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  const buildUpdatedJobOrder = (values: any) => {
    const jobOrder: JobOrder = {
      createdDate: values.createdDate,
      fillByDate: values.fillByDate,
      smis: values.smis,
      vessel: values.vessel?.id ? values.vessel : undefined,
      assigneeId: values.assignee?.id || undefined,
      onsigner: values.onsigner?.id ? values.onsigner : undefined,
      offsigner: values.offsigner?.id ? values.offsigner : undefined,
      onsignerOnboarded: jobOrderFromApi?.onsignerOnboarded,
      onsignerLeavingFrom: values.onsignerLeavingFrom
        ? values.onsignerLeavingFrom
        : undefined,
      onsignerDestination: values.onsignerDestination
        ? values.onsignerDestination
        : undefined,
      onsignerTravelTimestamp: values.onsignerTravelTimestamp
        ? values.onsignerTravelTimestamp
        : undefined,
      onsignerAccomodation: values.onsignerAccomodation
        ? values.onsignerAccomodation
        : undefined,
      onsignerPickupDate: values.onsignerPickupDate
        ? values.onsignerPickupDate
        : undefined,
      onsignerDropoffDate: values.onsignerDropoffDate
        ? values.onsignerDropoffDate
        : undefined,
      onsignerNotes: values.onsignerNotes ? values.onsignerNotes : undefined,
      onsignerUnionHall: values.onsignerUnionHall
        ? values.onsignerUnionHall
        : undefined,
      offsignerDeboarded: jobOrderFromApi?.offsignerDeboarded,
      offsignerLeavingFrom: values.offsignerLeavingFrom
        ? values.offsignerLeavingFrom
        : undefined,
      offsignerDestination: values.offsignerDestination
        ? values.offsignerDestination
        : undefined,
      offsignerTravelTimestamp: values.offsignerTravelTimestamp
        ? values.offsignerTravelTimestamp
        : undefined,
      offsignerAccomodation: values.offsignerAccomodation
        ? values.offsignerAccomodation
        : undefined,
      offsignerPickupDate: values.offsignerPickupDate
        ? values.offsignerPickupDate
        : undefined,
      offsignerDropoffDate: values.offsignerDropoffDate
        ? values.offsignerDropoffDate
        : undefined,
      offsignerNotes: values.offsignerNotes ? values.offsignerNotes : undefined,
      checklistRoot: values.checklistRoot?.id
        ? values.checklistRoot
        : undefined,
      travelDocuments:
        values.travelDocuments.length === 0
          ? undefined
          : values.travelDocuments,
      rating: values.rating,
      port: values.port ? values.port : undefined,
      reliefReason: values.reliefReason ? values.reliefReason : undefined,
      flightNotice:
        values.flightNotice !== undefined ? values.flightNotice : undefined,
      reliefTime: values.reliefTime ? values.reliefTime : undefined,
      covid19Testing: values.covid19Testing || undefined,
      testingLocation: values.testingLocation || undefined,
      jobType: values.jobType || undefined,
      publish: values.publish ? values.publish : undefined,
      deleted: !!jobOrderFromApi?.deleted,
      note: values.note || undefined,
      extra: values.extra || false,
      lastUpdated: jobOrderFromApi?.lastUpdated,
    };
    return jobOrder;
  };

  async function _submitForm(values: any, actions: any) {
    let jobOrderId = Number(id);
    if (!!id && id !== "new") {
      const updateResult = await updateJobOrder({
        jobOrderId: id,
        jobOrder: buildUpdatedJobOrder(values),
      });
      if ("error" in updateResult) {
        actions.setSubmitting(false);
        return;
      }
    } else {
      const createResult = await createJobOrder(buildUpdatedJobOrder(values));
      if ("error" in createResult) {
        actions.setSubmitting(false);
        return;
      }
      jobOrderId = createResult.data.id as number;
    }
    actions.setSubmitting(false);
    !id || id === "new" ? navigate(`/jobOrders/${jobOrderId}`) : refetch();
    toast.success(
      <p>
        Job Order {!!id && id !== "new" ? "updated" : "created"} successfully.
      </p>,
      {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      }
    );
    setShowJobOrderHistory(false);
  }

  function _handleSubmit(values: any, actions: any) {
    if (!id || id === "new") {
      actions.resetForm({ values });
      actions.setSubmitting(true);
    }
    _submitForm(values, actions);
  }

  const calcDaysOnboard = (date: string) => {
    const today = moment(new Date());
    return today.diff(moment(new Date(date)), "days");
  };

  const IsFormDirty = () => {
    const { dirty } = useFormikContext();
    usePrompt("Changes will not be saved, would you like to leave?", dirty);
    return <></>;
  };

  const handleOpen = () => {
    setConfirmDelete(true);
  };

  const handleClose = () => {
    setConfirmDelete(false);
  };

  const handleJobOrderDelete = async () => {
    if (!id || id === "new") return;
    if (!currDeletedNote) {
      toast.error(<p>Note is required for deleted Job Orders.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    const response = await markJobOrderDeleted({
      jobOrderId: id,
      note: currDeletedNote,
    });
    if (!("error" in response)) {
      toast.success(<p>Job Order successfully marked for deletion</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    handleClose();
    refetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }
  async function adminRegisterUser(profileId: string) {
    if (!!profileId && profileId !== "new") {
      const res = await adminCreateProfileUser(profileId);
      if ("error" in res) {
        return;
      }
    } else {
      toast.error(<p>Profile must have a saved email before registering.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    toast.success(<p>Registration email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
    refetch();
  }

  async function resendRegistrationEmail(profileId: string) {
    if (!!profileId && profileId !== "new") {
      const res = await adminResendRegistrationEmail(profileId);
      if ("error" in res) {
        return;
      }
    } else {
      toast.error(<p>Profile must have a saved email before registering.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    toast.success(<p>Registration email re-sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
    refetch();
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          _handleSubmit(values, actions);
        }}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form id={formId}>
            <IsFormDirty />
            <div className="page-header-between job-header-width align-items-center">
              <div className="flex gc-3">
                <h1>Job Order</h1>
                <div className="flex gc-3">
                  {!isViewOnly && (
                    <Button
                      variant="contained"
                      className="blue-button-small ht-4px"
                      type="submit"
                      form="jobOrderForm"
                      disabled={isSubmitting}
                    >
                      {" "}
                      {isSubmitting === true ? (
                        <CircularProgress className="flex align-items-center color-white wd-2px" />
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  )}
                  {!isViewOnly &&
                    id &&
                    id !== "new" &&
                    !jobOrderFromApi?.deleted &&
                    !jobOrderFromApi?.checklistRoot?.completed && (
                      <Button
                        className="grey-link-button"
                        onClick={() => handleOpen()}
                      >
                        <Delete />
                        Delete Job Order
                      </Button>
                    )}
                </div>
              </div>
              <div className="flex align-items-center justify-content-end">
                <Button
                  className="blue-link-button"
                  onClick={() => navigate(-1)}
                >
                  {" "}
                  <ArrowBack />
                  Back
                </Button>
              </div>

              <Dialog
                open={confirmDelete}
                onClose={handleClose}
                aria-labelledby="modal-title"
              >
                <Typography variant="h3" id="modal-title">
                  Delete Job Order
                </Typography>
                <p className="delete-paragraph">
                  Are you sure you would like to delete this Job Order?
                </p>
                <label>
                  <span>Note:</span>
                  <textarea
                    id="currDeletedNote"
                    value={currDeletedNote}
                    onChange={(e) => setCurrDeletedNote(e.target.value)}
                    maxLength={1000}
                  />
                </label>
                <div className="modal-footer">
                  <Button
                    className="outline-button-small"
                    onClick={handleJobOrderDelete}
                  >
                    Delete
                  </Button>
                  <Button className="blue-button-small" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </Dialog>
            </div>

            <main>
              <Paper className="order-header-wrap">
                <div className="order-header">
                  <label>
                    <span className="required">{assignee.label}</span>
                    <AutocompleteField
                      name={assignee.name}
                      data={assignees}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(assignee: User) => {
                        return assignee.name || assignee.email;
                      }}
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span className="required">{fillByDate.label}</span>
                    <InputField
                      name={fillByDate.name}
                      fullWidth
                      size="small"
                      type="date"
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span className="required">{vessel.label}</span>
                    <AutocompleteField
                      name={vessel.name}
                      data={!!vessels ? vessels.filter((v) => !v.disabled) : []}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(vessel: { name: any }) => {
                        return vessel?.name ? vessel.name : "";
                      }}
                      disabled={
                        isViewOnly || (id && id !== "new") ? true : false
                      }
                    />
                  </label>
                  <label>
                    <span className="required">{port.label}</span>
                    <AutocompleteField
                      name={port.name}
                      data={
                        !!values.vessel?.ports ? values.vessel.ports : ports
                      }
                      setFieldValue={setFieldValue}
                      getOptionLabel={(port: string) => (port ? port : "")}
                      isOptionEqualToValue={(opt: string, val: string) =>
                        opt === val
                      }
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span className="required">{rating.label}</span>
                    <AutocompleteField
                      name={rating.name}
                      data={Object.keys(JobRating).filter((r) =>
                        isABVariantJobOrder && id && id !== "new"
                          ? AB_VARIANT_RATINGS.has(JobRating[r as keyof typeof JobRating])
                          : !DisabledJobRatings.has(JobRating[r as keyof typeof JobRating])
                      )}
                      setFieldValue={setFieldValue}
                      getOptionLabel={(option: string) =>
                        JobRatingLabel[
                          JobRating[option as keyof typeof JobRating]
                        ]
                      }
                      isOptionEqualToValue={(opt: string, val: string) =>
                        opt === val
                      }
                      disabled={
                        isViewOnly || (id !== undefined && id !== "new" && !isABVariantJobOrder)
                      }
                    />
                  </label>
                  {!!id && id !== "new" && (
                    <label>
                      <span>Job Order ID</span>
                      <OutlinedInput value={id} disabled={true} />
                    </label>
                  )}
                  <label>
                    <span>{publish.label}</span>
                    <SelectField
                      name={publish.name}
                      hideBlank
                      data={Object.entries(Publish).map(([key, value]) => ({
                        value: value,
                        label: PublishLabel[key as Publish],
                      }))}
                      fullWidth
                      size="small"
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span>{createdDate.label}</span>
                    <InputField
                      name={createdDate.name}
                      fullWidth
                      size="small"
                      type="date"
                      disabled
                    />
                  </label>
                  <label>
                    <span>{smis.label}</span>
                    <InputField
                      name={smis.name}
                      fullWidth
                      size="small"
                      type="text"
                      disabled={isViewOnly}
                    />
                  </label>
                  <label>
                    <span className="required">{jobType.label}</span>
                    <SelectField
                      name={jobType.name}
                      hideBlank={id !== undefined && id !== "new"}
                      data={
                        RatingTypeMap.get(
                          JobRating[values.rating as keyof typeof JobRating]
                        ) === MarinerType.LICENSED
                          ? Object.entries(LicensedJobType).map(
                              ([key, value]) => ({
                                value: value,
                                label:
                                  LicensedJobTypeLabel[key as LicensedJobType],
                              })
                            )
                          : Object.entries(UnLicensedJobType).map(
                              ([key, value]) => ({
                                value: value,
                                label:
                                  UnLicensedJobTypeLabel[
                                    key as UnLicensedJobType
                                  ],
                              })
                            )
                      }
                      fullWidth
                      size="small"
                      disabled={isViewOnly}
                    />
                  </label>
                  <Paper className="p-1 flex align-items-center mark-extra">
                    <label className="extra-checkbox flex-row align-items-center g-1">
                      <Field name={extra.name} type="checkbox" />
                      <span>{extra.label}</span>
                    </label>
                  </Paper>
                </div>
              </Paper>
              <div className="job-order">
                <div className="checklist-section">
                  <div>
                    {/* Checklist */}
                    {!isViewOnly && values.checklistRoot && (
                      <ChecklistDisplay
                        root={values.checklistRoot}
                        userId={user?.username}
                        requiredCourseNames={requiredCourseNames}
                        blockNaCourseNames={blockNaCourseNames}
                      />
                    )}
                    {isViewOnly && values.checklistRoot && (
                      <JobRequirementsDisplay
                        root={values.checklistRoot}
                        userId={user?.username}
                      />
                    )}
                    {!values.checklistRoot && (
                      <Paper className="checklist placeholder">
                        <CheckCircle />
                        <p>Save this job order to begin tracking progress</p>
                      </Paper>
                    )}
                  </div>

                  <Paper className="deletion-note">
                    <label>
                      <h3>{note.label}</h3>
                      <TextAreaField
                        name={note.name}
                        size="small"
                        maxLength={1000}
                        disabled={isViewOnly}
                      />
                    </label>
                  </Paper>
                </div>
                <div className="order-details">
                  {/* Onsigner */}
                  <Paper>
                    <div className="page-header">
                      <h3>Onsigner</h3>
                      <div className="flex gc-3">
                        {!isViewOnly && (
                          <Tooltip title="Coming Soon">
                            <Button className="grey-link-button">
                              <Call />
                              Call
                            </Button>
                          </Tooltip>
                        )}
                        {!isViewOnly &&
                          (!isLoading &&
                          !!jobOrderFromApi?.onsigner?.email &&
                          values.onsigner?.email ===
                            jobOrderFromApi?.onsigner?.email ? (
                            <EmailModal
                              key={EmailType.EMAIL_TO_MARINER + "_onsigner"}
                              vesselId={jobOrderFromApi?.vessel?.id}
                              email={jobOrderFromApi?.onsigner?.email}
                              cc={
                                unionHalls?.find(
                                  (hall) =>
                                    hall.name ===
                                    jobOrderFromApi?.onsignerUnionHall
                                )?.emails
                              }
                              emailType={EmailType.EMAIL_TO_MARINER}
                              parentCallback={handleEmailCallBack}
                              jobOrderId={!!id && !!+id ? +id : undefined}
                              action="Sent to Onsigner"
                            >
                              <Button className="blue-link-button">
                                <Email />
                                Email
                              </Button>
                            </EmailModal>
                          ) : (
                            <Button className="grey-link-button">
                              <Email />
                              Email
                            </Button>
                          ))}
                      </div>
                    </div>
                    <div className="form-narrow mb-2">
                      <label htmlFor="onsigner">
                        <span>{onsigner.label}</span>
                        <MarinerAutocompleteField
                          id="onsignerAutocomplete"
                          name={onsigner.name}
                          marinerRating={
                            values.rating ? values.rating : undefined
                          }
                          setFieldValue={setFieldValue}
                          onChangeCallback={(value: MarinerProfile) =>
                            handleSignerCallback(
                              SignerType.OnSigner,
                              value,
                              setFieldValue
                            )
                          }
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{onsignerAgentUnion.label}</span>
                        <InputField
                          name={onsignerAgentUnion.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled
                        />
                      </label>
                      <label>
                        <span>{onsignerUnionHall.label}</span>
                        <AutocompleteField
                          name={onsignerUnionHall.name}
                          data={unionHalls?.map((union) => union.name)}
                          setFieldValue={setFieldValue}
                          getOptionLabel={(option: string) =>
                            option ? option : ""
                          }
                          isOptionEqualToValue={(opt: string, val: string) =>
                            opt === val
                          }
                          disabled={isViewOnly}
                          // Need to change so that email is updated onClick
                        />
                      </label>
                    </div>
                    {/* Registration Status */}
                    {!isMariner &&
                      values.onsigner?.id &&
                      // hide this section for profiles where no action is needed
                      onsignerRegistrationStatus !==
                        RegistrationStatus.CONFIRMED && (
                        <div className="registration-status">
                          {marinerStatusText(onsignerRegistrationStatus)}
                          {registrationEmailButton(
                            onsignerRegistrationStatus,
                            onsignerEmail,
                            () => adminRegisterUser(values.onsigner.id),
                            () => resendRegistrationEmail(values.onsigner.id),
                            isViewOnly
                          )}
                        </div>
                      )}
                    {/* Documents */}
                    {values.onsigner?.id && (
                      <div className="mb-2">
                        <h3>Profile Documents</h3>
                        <h4>Uploaded Documents</h4>
                        <ProfileDocuments
                          profileId={+values.onsigner.id}
                          isViewOnly={isViewOnly}
                          actionByType={ActionByType.JobOrder}
                          parentCallback={handleProfileDocumentCallback}
                        />
                      </div>
                    )}
                    {showCovidFields && (
                      <div id="covid-section">
                        <h3>COVID-19</h3>
                        <div className="form-narrow">
                          <label>
                            <span>{covid19Testing.label}</span>
                            <InputField
                              name={covid19Testing.name}
                              fullWidth
                              size="small"
                              type="text"
                              disabled={isViewOnly}
                            />
                          </label>

                          <label>
                            <span>{testingLocation.label}</span>
                            <InputField
                              name={testingLocation.name}
                              fullWidth
                              size="small"
                              type="text"
                              disabled={isViewOnly}
                            />
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="page-header">
                      <h3>Onsigner Travel</h3>
                      {!isViewOnly && (
                        <EmailModal
                          key={EmailType.EMAIL_TO_TRAVEL_AGENT + "_onsigner"}
                          vesselId={jobOrderFromApi?.vessel?.id}
                          jobOrderId={!!id && !!+id ? +id : undefined}
                          action="Sent to Onsigner Travel Agent"
                          emailData={{
                            vesselName: jobOrderFromApi?.vessel?.name as string,
                            passenger:
                              jobOrderFromApi?.onsigner?.firstName +
                              " " +
                              jobOrderFromApi?.onsigner?.middleName +
                              " " +
                              jobOrderFromApi?.onsigner?.lastName,
                            gender:
                              GenderSymbol[
                                jobOrderFromApi?.onsigner?.gender as Gender
                              ],
                            dob:
                              jobOrderFromApi?.onsigner?.dateOfBirth ===
                                undefined ||
                              jobOrderFromApi?.onsigner?.dateOfBirth === null
                                ? "null"
                                : formatDateString(
                                    jobOrderFromApi?.onsigner?.dateOfBirth
                                  ),
                            twicTsaNum: jobOrderFromApi?.onsigner
                              ?.twicTsaNum as string,
                            from: jobOrderFromApi?.onsignerLeavingFrom as string,
                            to: jobOrderFromApi?.onsignerDestination as string,
                            date:
                              jobOrderFromApi?.fillByDate === undefined ||
                              jobOrderFromApi?.fillByDate === null
                                ? "null"
                                : formatDateString(jobOrderFromApi?.fillByDate),
                            time: jobOrderFromApi?.onsignerTravelTimestamp as string,
                            hotelOrRental:
                              jobOrderFromApi?.onsignerAccomodation as string,
                            pickUpDate:
                              jobOrderFromApi?.onsignerPickupDate ===
                                undefined ||
                              jobOrderFromApi?.onsignerPickupDate === null
                                ? "null"
                                : formatDateString(
                                    jobOrderFromApi?.onsignerPickupDate
                                  ),
                            dropOffDate:
                              jobOrderFromApi?.onsignerDropoffDate ===
                                undefined ||
                              jobOrderFromApi?.onsignerDropoffDate === null
                                ? "null"
                                : formatDateString(
                                    jobOrderFromApi?.onsignerDropoffDate
                                  ),
                            phone: jobOrderFromApi?.onsigner?.phone as string,
                          }}
                          email={undefined}
                          emailType={EmailType.EMAIL_TO_TRAVEL_AGENT}
                          parentCallback={handleEmailCallBack}
                        >
                          <Button className="blue-link-button">
                            <Email />
                            Email Travel Agent
                          </Button>
                        </EmailModal>
                      )}
                    </div>
                    <div className="form-narrow">
                      <label>
                        <span>{onsignerLeavingFrom.label}</span>
                        <InputField
                          name={onsignerLeavingFrom.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{onsignerDestination.label}</span>
                        <InputField
                          name={onsignerDestination.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{onsignerAccomodation.label}</span>
                        <InputField
                          name={onsignerAccomodation.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{onsignerTwicTsaNum.label}</span>
                        <InputField
                          name={onsignerTwicTsaNum.name}
                          fullWidth
                          size="small"
                          type="text"
                          disabled
                        />
                      </label>
                      <label>
                        <span>{onsignerDateOfBirth.label}</span>
                        <InputField
                          name={onsignerDateOfBirth.name}
                          fullWidth
                          size="small"
                          type="date"
                          disabled
                        />
                      </label>
                      <label>
                        <span>{onsignerTravelTimestamp.label}</span>
                        <InputField
                          name={onsignerTravelTimestamp.name}
                          fullWidth
                          size="small"
                          type="time"
                          disabled={isViewOnly}
                        />
                      </label>

                      <label>
                        <span>{onsignerPickupDate.label}</span>
                        <InputField
                          name={onsignerPickupDate.name}
                          fullWidth
                          size="small"
                          type="date"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{onsignerDropoffDate.label}</span>
                        <InputField
                          name={onsignerDropoffDate.name}
                          fullWidth
                          size="small"
                          type="date"
                          disabled={isViewOnly}
                        />
                      </label>
                      <label>
                        <span>{flightNotice.label}</span>
                        <SelectField
                          name={flightNotice.name}
                          data={[
                            { value: true, label: "Y" },
                            { value: false, label: "N" },
                          ]}
                          fullWidth
                          size="small"
                          disabled={isViewOnly}
                        />
                      </label>
                    </div>
                    {id !== undefined && !isNaN(+id) && (
                      <>
                        <h4>Travel Documents</h4>
                        <TravelDocuments
                          jobOrderId={+id}
                          signer={SignerType.OnSigner}
                          emailTo={jobOrderFromApi?.onsigner?.email}
                          unionHallEmails={
                            unionHalls?.find(
                              (hall) =>
                                hall.name === jobOrderFromApi?.onsignerUnionHall
                            )?.emails
                          }
                          validEmail={
                            values.onsigner?.email ===
                            jobOrderFromApi?.onsigner?.email
                          }
                          isViewOnly={isViewOnly}
                          parentCallback={handleTravelDocumentCallback}
                        />
                      </>
                    )}
                    <label className="mt-2">
                      <h3>{onsignerNotes.label}</h3>
                      <TextAreaField
                        name={onsignerNotes.name}
                        size="small"
                        disabled={isViewOnly}
                      />
                    </label>
                  </Paper>
                  {/* Offsigner */}
                  {id !== undefined &&
                    !isNaN(+id) &&
                    !!jobOrderFromApi?.offsigner && (
                      <Paper>
                        <div className="page-header">
                          <h3>Offsigner</h3>
                          {!isViewOnly && (
                            <Tooltip title="Coming Soon">
                              <Button className="grey-link-button">
                                <Call />
                                Call
                              </Button>
                            </Tooltip>
                          )}
                          {!isViewOnly &&
                            (!isLoading &&
                            !!jobOrderFromApi?.offsigner?.email &&
                            values.offsigner?.email ===
                              jobOrderFromApi?.offsigner?.email ? (
                              <EmailModal
                                key={EmailType.EMAIL_TO_MARINER + "_offsigner"}
                                vesselId={jobOrderFromApi?.vessel?.id}
                                email={jobOrderFromApi.offsigner?.email}
                                emailType={EmailType.EMAIL_TO_MARINER}
                                parentCallback={handleEmailCallBack}
                                jobOrderId={!!id && !!+id ? +id : undefined}
                                action="Sent to Offsigner"
                              >
                                <Button className="blue-link-button">
                                  <Email />
                                  Email
                                </Button>
                              </EmailModal>
                            ) : (
                              <Button className="grey-link-button">
                                <Email />
                                Email
                              </Button>
                            ))}
                        </div>
                        <div className="form-narrow">
                          <label htmlFor="offsigner">
                            <span>{offsigner.label}</span>
                            <MarinerAutocompleteField
                              id="offsignerAutocomplete"
                              name={offsigner.name}
                              marinerRating={
                                values.rating ? values.rating : undefined
                              }
                              setFieldValue={setFieldValue}
                              onChangeCallback={(value: MarinerProfile) =>
                                handleSignerCallback(
                                  SignerType.OnSigner,
                                  value,
                                  setFieldValue
                                )
                              }
                              disabled={
                                isViewOnly || !!jobOrderFromApi.offsigner?.id
                              }
                            />
                          </label>
                          <label>
                            <span>{reliefReason.label}</span>
                            <SelectField
                              name={reliefReason.name}
                              data={Object.entries(ReliefReason).map(
                                ([key, value]) => ({
                                  value: value,
                                  label: ReliefReasonLabel[key as ReliefReason],
                                })
                              )}
                              hideBlank
                              fullWidth
                              size="small"
                              disabled={isViewOnly}
                            />
                          </label>
                          <label htmlFor="daysOnboard">
                            <span>Days Onboard</span>
                            <input
                              form="jobOrderForm"
                              id="daysOnboard"
                              value={
                                dispatch &&
                                dispatch[0] &&
                                dispatch[0]?.startDate
                                  ? calcDaysOnboard(dispatch[0].startDate)
                                  : ""
                              }
                              disabled={true}
                            />
                          </label>
                          <label>
                            <span>{reliefTime.label}</span>
                            <InputField
                              name={reliefTime.name}
                              fullWidth
                              size="small"
                              type="text"
                              disabled={isViewOnly}
                            />
                          </label>
                        </div>
                        {/* Registration Status */}
                        {!isMariner &&
                          values.offsigner?.id &&
                          // hide this section for profiles where no action is needed
                          offsignerRegistrationStatus !==
                            RegistrationStatus.CONFIRMED && (
                            <div className="registration-status">
                              {marinerStatusText(offsignerRegistrationStatus)}
                              {registrationEmailButton(
                                offsignerRegistrationStatus,
                                offsignerEmail,
                                () => adminRegisterUser(values.offsigner.id),
                                () =>
                                  resendRegistrationEmail(values.offsigner.id),
                                isViewOnly
                              )}
                            </div>
                          )}
                        <div className="page-header">
                          <h3>Offsigner Travel</h3>
                          {!isViewOnly && (
                            <EmailModal
                              key={
                                EmailType.EMAIL_TO_TRAVEL_AGENT + "_offsigner"
                              }
                              vesselId={jobOrderFromApi?.vessel?.id}
                              jobOrderId={!!id && !!+id ? +id : undefined}
                              action="Sent to Offsigner Travel Agent"
                              emailData={{
                                vesselName: jobOrderFromApi?.vessel
                                  ?.name as string,
                                passenger:
                                  jobOrderFromApi?.offsigner?.firstName +
                                  " " +
                                  jobOrderFromApi?.offsigner?.middleName +
                                  " " +
                                  jobOrderFromApi?.offsigner?.lastName,
                                gender:
                                  GenderSymbol[
                                    jobOrderFromApi?.offsigner?.gender as Gender
                                  ],
                                dob: formatDateString(
                                  jobOrderFromApi?.offsigner?.dateOfBirth
                                ),
                                twicTsaNum: jobOrderFromApi?.offsigner
                                  ?.twicTsaNum as string,
                                from: jobOrderFromApi?.offsignerLeavingFrom as string,
                                to: jobOrderFromApi?.offsignerDestination as string,
                                date: formatDateString(
                                  jobOrderFromApi?.fillByDate
                                ),
                                time: jobOrderFromApi?.offsignerTravelTimestamp as string,
                                hotelOrRental:
                                  jobOrderFromApi?.offsignerAccomodation as string,
                                pickUpDate:
                                  jobOrderFromApi?.offsignerPickupDate ===
                                    undefined ||
                                  jobOrderFromApi?.offsignerPickupDate === null
                                    ? "null"
                                    : formatDateString(
                                        jobOrderFromApi?.offsignerPickupDate
                                      ),
                                dropOffDate:
                                  jobOrderFromApi?.offsignerDropoffDate ===
                                    undefined ||
                                  jobOrderFromApi?.offsignerDropoffDate === null
                                    ? "null"
                                    : formatDateString(
                                        jobOrderFromApi?.offsignerDropoffDate
                                      ),
                                phone: jobOrderFromApi?.offsigner
                                  ?.phone as string,
                              }}
                              email={undefined}
                              emailType={EmailType.EMAIL_TO_TRAVEL_AGENT}
                              parentCallback={handleEmailCallBack}
                            >
                              <Button className="blue-link-button">
                                <Email />
                                Email Travel Agent
                              </Button>
                            </EmailModal>
                          )}
                        </div>
                        <div className="form-narrow">
                          <label>
                            <span>{offsignerLeavingFrom.label}</span>
                            <AutocompleteField
                              name={offsignerLeavingFrom.name}
                              data={ports}
                              setFieldValue={setFieldValue}
                              getOptionLabel={(port: string) =>
                                port ? port : ""
                              }
                              isOptionEqualToValue={(
                                opt: string,
                                val: string
                              ) => opt === val}
                              disabled={isViewOnly}
                            />
                          </label>
                          <label>
                            <span>{offsignerDestination.label}</span>
                            <InputField
                              name={offsignerDestination.name}
                              fullWidth
                              size="small"
                              type="text"
                              disabled={isViewOnly}
                            />
                          </label>
                          <label>
                            <span>{offsignerAccomodation.label}</span>
                            <InputField
                              name={offsignerAccomodation.name}
                              fullWidth
                              size="small"
                              type="text"
                              disabled={isViewOnly}
                            />
                          </label>
                          <label>
                            <span>{offsignerTwicTsaNum.label}</span>
                            <InputField
                              name={offsignerTwicTsaNum.name}
                              fullWidth
                              size="small"
                              type="text"
                              disabled
                            />
                          </label>
                          <label>
                            <span>{offsignerDateOfBirth.label}</span>
                            <InputField
                              name={offsignerDateOfBirth.name}
                              fullWidth
                              size="small"
                              type="date"
                              disabled
                            />
                          </label>
                          <label>
                            <span>{offsignerTravelTimestamp.label}</span>
                            <InputField
                              name={offsignerTravelTimestamp.name}
                              fullWidth
                              size="small"
                              type="time"
                              disabled={isViewOnly}
                            />
                          </label>

                          <label>
                            <span>{offsignerPickupDate.label}</span>
                            <InputField
                              name={offsignerPickupDate.name}
                              fullWidth
                              size="small"
                              type="date"
                              disabled={isViewOnly}
                            />
                          </label>
                          <label>
                            <span>{offsignerDropoffDate.label}</span>
                            <InputField
                              name={offsignerDropoffDate.name}
                              fullWidth
                              size="small"
                              type="date"
                              disabled={isViewOnly}
                            />
                          </label>
                        </div>
                        {id !== undefined && !isNaN(+id) && (
                          <>
                            <h4>Travel Documents</h4>
                            <TravelDocuments
                              jobOrderId={+id}
                              signer={SignerType.OffSigner}
                              emailTo={jobOrderFromApi?.offsigner?.email}
                              validEmail={
                                values.offsigner?.email ===
                                jobOrderFromApi?.offsigner?.email
                              }
                              isViewOnly={isViewOnly}
                              parentCallback={handleTravelDocumentCallback}
                            />
                          </>
                        )}
                        <label className="mt-2">
                          <h3>{offsignerNotes.label}</h3>
                          <TextAreaField
                            name={offsignerNotes.name}
                            size="small"
                            disabled={isViewOnly}
                          />
                        </label>
                      </Paper>
                    )}
                  <Paper>
                    <div className="page-header justify-content-btwn">
                      <h3>Job Order History</h3>
                      {!showJobOrderHistory ? (
                        <div className="flex align-items-center justify-content-end">
                          <Button
                            className="blue-link-button"
                            onClick={() => {
                              if (id !== undefined && id !== "new") {
                                getJobOrderChanges(id);
                              }
                              setShowJobOrderHistory(!showJobOrderHistory);
                            }}
                          >
                            {" "}
                            Expand
                            <ExpandMoreIcon />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex align-items-center justify-content-end">
                          <Button
                            className="blue-link-button"
                            onClick={() =>
                              setShowJobOrderHistory(!showJobOrderHistory)
                            }
                          >
                            {" "}
                            Collapse
                            <ExpandLessIcon />
                          </Button>
                        </div>
                      )}
                      {showJobOrderHistory && (
                        <>
                          <TableContainer>
                            <Table className="table">
                              <TableHead>
                                <TableRow>
                                  <TableCell align="center">
                                    <>User</>
                                  </TableCell>
                                  <TableCell>
                                    <>Date</>
                                  </TableCell>
                                  <TableCell>
                                    <>Field Name</>
                                  </TableCell>
                                  <TableCell>
                                    <>Description</>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {jobOrderChangeList === undefined ? (
                                  <TableRow>
                                    <TableCell colSpan={4}>
                                      <Box sx={{ display: "flex" }}>
                                        <CircularProgress />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  jobOrderChangeList?.map((row, index) => {
                                    return (
                                      <TableRow key={"jobOrder" + index}>
                                        <TableCell className="centered">
                                          <Tooltip
                                            title={
                                              row?.actionByName ||
                                              row?.actionByEmail ||
                                              "Deleted user"
                                            }
                                            placement="right"
                                          >
                                            <Avatar
                                              sx={{
                                                bgcolor: colorFromName(
                                                  row?.actionByName || ""
                                                ),
                                                height: 30,
                                                width: 30,
                                              }}
                                            >
                                              {!!row?.actionByName
                                                ? row?.actionByName.substring(
                                                    0,
                                                    1
                                                  )
                                                : undefined}
                                            </Avatar>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell className="centered">
                                          <Tooltip
                                            title={
                                              !!row.actionTimestamp
                                                ? new Date(
                                                    row.actionTimestamp.toString()
                                                  ).toLocaleString()
                                                : ""
                                            }
                                          >
                                            <span>
                                              {!!row.actionTimestamp
                                                ? formatDateString(
                                                    row.actionTimestamp?.toString()
                                                  )
                                                : ""}
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell className="centered">
                                          {row.fieldName === FieldName.empty
                                            ? row.logType
                                            : row.logType +
                                              " - " +
                                              FieldNameLabel[
                                                row.fieldName as FieldName
                                              ]}
                                        </TableCell>
                                        <TableCell className="centered">
                                          {row.fieldName === FieldName.empty
                                              ? row.newVal
                                              : convertFieldValueToDescription(row.fieldName, row.oldVal) +
                                            " -> " +
                                              convertFieldValueToDescription(row.fieldName, row.newVal)}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}
                    </div>
                  </Paper>
                </div>
              </div>
            </main>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default JobOrderForm;
